import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';

const WA_LINK = 'https://wa.me/554891802091';

const geomembraneProductSlugs = ['bt800', 'bt950', 'cx300-1400w', 'wgw300'];

const selectionFactors = [
  { icon: 'ri-stack-line', title: 'Material', desc: 'PEAD, PELBD, PVC ou PP — cada material exige parâmetros específicos de temperatura e pressão.' },
  { icon: 'ri-ruler-line', title: 'Espessura', desc: 'De 500 a 3000 mic — quanto maior a espessura, maior a potência necessária.' },
  { icon: 'ri-tools-line', title: 'Tipo de Soldagem', desc: 'Cunha quente para sobreposição contínua ou extrusão para reparos e detalhes.' },
  { icon: 'ri-speed-line', title: 'Velocidade', desc: 'De 2 a 9 m/min — impacta diretamente na produtividade da obra.' },
  { icon: 'ri-bar-chart-line', title: 'Produtividade', desc: 'Metros lineares por dia — essencial para cumprir cronogramas.' },
  { icon: 'ri-building-line', title: 'Características da Obra', desc: 'Escala do projeto, acesso ao local e condições de operação.' },
];

const brandMap: Record<string, string> = {
  bt800: 'SoldaGeo',
  bt950: 'SoldaGeo',
  'cx300-1400w': 'Weldy',
  wgw300: 'Weldy',
};

const shortDescMap: Record<string, string> = {
  bt800: 'Compacto e leve, ideal para obras de menor escala e locais de difícil acesso. 800W com controle de temperatura digital.',
  bt950: 'Alta performance para grandes projetos. 2100W, velocidade de até 8 m/min e controle digital de temperatura.',
  'cx300-1400w': 'Precisão suíça em equipamento compacto. 1400W com controle digital para soldas de alta qualidade.',
  wgw300: 'Máxima produtividade com 2100W e velocidade de até 9 m/min. Robusto para operação contínua em campo.',
};

const keySpecsMap: Record<string, { label: string; value: string }[]> = {
  bt800: [
    { label: 'Potência', value: '800W / 220V' },
    { label: 'Velocidade', value: '2–5 m/min' },
    { label: 'Temperatura', value: '0–400°C' },
    { label: 'Espessura', value: '500–1500 mic' },
  ],
  bt950: [
    { label: 'Potência', value: '2100W / 220V' },
    { label: 'Velocidade', value: '5–8 m/min' },
    { label: 'Temperatura', value: '0–500°C' },
    { label: 'Espessura', value: '500–3000 mic' },
  ],
  'cx300-1400w': [
    { label: 'Potência', value: '1400W / 220V' },
    { label: 'Velocidade', value: '0,5–6 m/min' },
    { label: 'Temperatura', value: '450°C' },
    { label: 'Espessura', value: '500–1800 mic' },
  ],
  wgw300: [
    { label: 'Potência', value: '2100W / 220V' },
    { label: 'Velocidade', value: '5–9 m/min' },
    { label: 'Temperatura', value: '500°C' },
    { label: 'Espessura', value: '500–3000 mic' },
  ],
};

export default function ProductShowcase() {
  const displayedProducts = products.filter((p) => geomembraneProductSlugs.includes(p.slug));

  const handleWhatsAppClick = (e: React.MouseEvent, productName: string) => {
    e.preventDefault();
    const url = `https://wa.me/554891802091?text=Ol%C3%A1!%20Tenho%20interesse%20no%20${encodeURIComponent(productName)}.%20Poderia%20me%20passar%20um%20or%C3%A7amento%3F`;
    if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
      (window as any).gtag_report_conversion(url, true);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <>
      {/* Selection Guide */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="text-green-600 text-xs font-black uppercase tracking-[0.2em]">
              Guia de Seleção
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase mt-3 mb-4">
              Encontre a Máquina de Solda Ideal
            </h2>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto leading-relaxed">
              A escolha do equipamento depende de fatores como material, espessura, tipo de soldagem, velocidade, produtividade e características da obra.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {selectionFactors.map((factor) => (
              <div
                key={factor.title}
                className="flex items-start gap-4 bg-white rounded-2xl p-5 border border-gray-100"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl flex-shrink-0">
                  <i className={`${factor.icon} text-green-600 text-lg`} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase mb-1">{factor.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{factor.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-16 md:py-20 bg-white" id="maquinas">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="text-green-600 text-xs font-black uppercase tracking-[0.2em]">
              Nossos Equipamentos
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 uppercase mt-3 mb-4">
              Máquinas de Solda de Geomembrana
            </h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto leading-relaxed">
              Equipamentos profissionais para soldagem de geomembranas PEAD, PELBD, PVC e outros termoplásticos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {displayedProducts.map((product) => {
              const specs = keySpecsMap[product.slug] || [];
              return (
                <div
                  key={product.slug}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-green-300 transition-all duration-300 group flex flex-col md:flex-row"
                  data-product-shop
                >
                  {/* Image */}
                  <div className="md:w-2/5 bg-gray-100 flex items-center justify-center p-6 relative overflow-hidden">
                    <img
                      src={product.image}
                      alt={`Máquina de solda de geomembrana ${product.name}`}
                      className="w-full h-48 md:h-full object-contain transition-transform duration-500 group-hover:scale-105"
                    />
                    {product.badge && (
                      <span className="absolute top-3 left-3 bg-green-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full tracking-wider">
                        {product.badge}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="md:w-3/5 p-5 md:p-6 flex flex-col justify-between">
                    <div>
                      <span className="text-green-600 text-[10px] font-black uppercase tracking-widest">
                        {brandMap[product.slug] || 'SoldaGeo'}
                      </span>
                      <h3 className="text-xl md:text-2xl font-black text-gray-900 uppercase mt-1 mb-2">
                        {product.name}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-4">
                        {shortDescMap[product.slug] || product.description}
                      </p>

                      {/* Key specs */}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        {specs.map((spec) => (
                          <div key={spec.label} className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                            <span className="text-gray-400 text-[10px] uppercase tracking-wide">{spec.label}: </span>
                            <span className="text-gray-800 text-[11px] font-black">{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Link
                        to={product.href}
                        className="flex items-center justify-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white text-xs font-black px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide flex-1"
                      >
                        <i className="ri-eye-line" />
                        Ver Produto
                      </Link>
                      <a
                        href={WA_LINK}
                        target="_blank"
                        rel="nofollow noreferrer"
                        onClick={(e) => handleWhatsAppClick(e, product.name)}
                        className="flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-black px-4 py-2.5 rounded-xl transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide flex-1"
                      >
                        <i className="ri-whatsapp-line" />
                        Solicitar Orçamento
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}